/**
 * PlaylistRemoveConfirm — single button that toggles between two visual
 * states for click-to-confirm delete:
 *
 *   1. IDLE      — red ✕ pill (matches `.playlist-edit__button--remove`).
 *      Click → enters CONFIRMING.
 *   2. CONFIRMING — teal-bordered ✓ pill (matches
 *      `.profile-my-playlists__sharing-badge--shared`).
 *      Click → fires `onRemove`, returns to IDLE.
 *
 * Cancel: clicking anywhere outside the button (other tiles, chevrons,
 * page background) returns to IDLE without removing the item.  We attach
 * a `mousedown` listener at the document level — `mousedown` fires before
 * any `click` handler, so sibling buttons (chevrons in the same toolbar)
 * still receive their own click while we cancel our pending state.
 */

import React, {
  useCallback, useEffect, useRef, useState,
} from 'react'
import Check from '../../../../Icon/icons/Check/index'
import Close from '../../../../Icon/icons/Close/index'

export interface PlaylistRemoveConfirmProps {
  /** Fired when the user confirms (clicks the ✓ state). */
  onRemove: () => void
  /** aria-label for the idle ✕ state.  Defaults to English. */
  removeLabel?: string
  /** aria-label for the confirming ✓ state.  Defaults to English. */
  confirmLabel?: string
  /** Pill + icon size.  'small' (default) = 26×26 / 18px icon — used in
   *  carousel rows on /library, /watch-later, etc.  'medium' = 32×32 / 22px
   *  for tablet-width grids.  'large' = 36×36 / 24px for desktop full-view. */
  size?: 'small' | 'medium' | 'large'
}

const ICON_SIZE_BY_VARIANT: Record<'small' | 'medium' | 'large', string> = {
  small: '18px',
  medium: '22px',
  large: '24px',
}

export function PlaylistRemoveConfirm ({
  onRemove,
  removeLabel = 'Remove from playlist',
  confirmLabel = 'Confirm remove',
  size = 'small',
}: PlaylistRemoveConfirmProps) {
  const [confirming, setConfirming] = useState(false)
  const rootRef = useRef<HTMLButtonElement>(null)
  const iconSize = ICON_SIZE_BY_VARIANT[size]

  // Cancel pending confirm when the user clicks anywhere outside this
  // button.  mousedown beats click in the event order so sibling controls
  // still process their own clicks.
  useEffect(() => {
    if (!confirming) return undefined
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setConfirming(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [confirming])

  // Escape cancels the pending confirm — keyboard equivalent of clicking
  // outside.  Listening at the document level lets users press Escape from
  // anywhere on the page (focus may be on the button or have moved away).
  useEffect(() => {
    if (!confirming) return undefined
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirming(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [confirming])

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (!confirming) {
      setConfirming(true)
      return
    }
    onRemove()
    setConfirming(false)
  }, [confirming, onRemove])

  const className = [
    'playlist-remove-confirm__btn',
    confirming
      ? 'playlist-remove-confirm__btn--confirming'
      : 'playlist-remove-confirm__btn--idle',
    `playlist-remove-confirm__btn--${size}`,
  ].join(' ')

  return (
    <button
      ref={rootRef}
      type="button"
      className={className}
      onClick={handleClick}
      aria-label={confirming ? confirmLabel : removeLabel}
      aria-pressed={confirming}
      title={confirming ? confirmLabel : removeLabel}
    >
      {confirming
        ? <Check size={iconSize} />
        : <Close size={iconSize} />}
    </button>
  )
}
